import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Table, Button, Form, Row, Col } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import useAuth from '../context/useAuth';

const MemberListReport = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageGroupSize] = useState(10); // Show only x pages at a time

    const [lifeTimeDonation, setLifeTimeDonation] = useState('');
    const [lastDonation, setLastDonation] = useState('');
    const [lastDonationDate, setLastDonationDate] = useState('');
    const [sortBy, setSortBy] = useState('first1');
    const { role, resultsPerPage } = useAuth();

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();
                if (lifeTimeDonation) queryParams.append('lifeTimeDonation', lifeTimeDonation);
                if (lastDonation) queryParams.append('lastDonation', lastDonation);
                if (lastDonationDate) queryParams.append('lastDonationDate', lastDonationDate);
                if (sortBy) queryParams.append('sortBy', sortBy);

                const response = await api.get(`/api/membersreport?${queryParams.toString()}`);
                setMembers(response.data); // Fetch all data once
                setCurrentPage(1); // Reset pagination to page 1 after new data fetch
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchMembers();
    }, [lifeTimeDonation, lastDonation, lastDonationDate, sortBy]);

    // Pagination logic
    const indexOfLastMember = currentPage * resultsPerPage;
    const indexOfFirstMember = indexOfLastMember - resultsPerPage;
    const currentMembers = members.slice(indexOfFirstMember, indexOfLastMember);

    const totalPages = Math.ceil(members.length / resultsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Page group calculation (first to last visible page)
    const getPageNumbers = () => {
        const startPage = Math.floor((currentPage - 1) / pageGroupSize) * pageGroupSize + 1;
        const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

        // Generate an array of page numbers in the current group
        return Array.from({ length: endPage - startPage + 1 }, (_, idx) => startPage + idx);
    };

    const handleFirstPage = () => setCurrentPage(1);
    const handleLastPage = () => setCurrentPage(totalPages);

    const handlePreviousPageGroup = () => {
        const newPage = Math.max(currentPage - pageGroupSize, 1);
        setCurrentPage(newPage);
    };

    const handleNextPageGroup = () => {
        const newPage = Math.min(currentPage + pageGroupSize, totalPages);
        setCurrentPage(newPage);
    };

    const getLocalTimestamp = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
    };

    // Function to generate dynamic title based on selected criteria
    const getReportTitle = () => {
        let title = 'Member Donation Report';

        if (lifeTimeDonation && lastDonation) {
            title += ` - Lifetime Donation: $${parseFloat(lifeTimeDonation).toLocaleString()}+ and Last Donation: $${parseFloat(lastDonation).toLocaleString()}+`;
        } else if (lifeTimeDonation) {
            title += ` - Lifetime Donation: $${parseFloat(lifeTimeDonation).toLocaleString()}+`;
        } else if (lastDonation) {
            title += ` - Last Donation: $${parseFloat(lastDonation).toLocaleString()}+`;
        }

        if (lastDonationDate) {
            title += ` in ${lastDonationDate}`;
        }

        return title;
    };
    /*
    const handlePrintAll = () => {
        window.print();
    }; */

    // Handle Print All function
    const handlePrintAll = () => {
        // Open a new window
        const printWindow = window.open('', '', 'height=600,width=800');

        // Check if the window is successfully opened
        if (printWindow) {
            // Create the HTML content
            const htmlContent = `
                <html>
                    <head>
                        <title>Print Report</title>
                        <style>
                            table { width: 100%; border-collapse: collapse; }
                            th, td { border: 1px solid black; padding: 8px; text-align: left; }
                            th { background-color: #f2f2f2; }
                        </style>
                    </head>
                    <body>
                        <h1>${getReportTitle()}</h1>
                        <table>
                            <thead>
                                <tr>
                                    <th>Member ID</th>
                                    <th>Member Name</th>
                                    <th>Address</th>
                                    <th>City</th>
                                    <th>State</th>
                                    <th>Zip</th>
                                    <th>LifeTime Donation</th>
                                    <th>Last Donation</th>
                                    <th>Last Donation Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${members.map(member => `
                                    <tr>
                                        <td>${member.memberId}</td>
                                        <td>${member.first1} ${member.middle1} ${member.last1}</td>
                                        <td>${member.address} ${member.address2}</td>
                                        <td>${member.city}</td>
                                        <td>${member.state}</td>
                                        <td>${member.zip}</td>
                                        <td>${member.totalDonation ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(member.totalDonation)) : '0'}</td>
                                        <td>${member.lastDonation ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(member.lastDonation)) : '0'}</td>
                                        <td>${member.lastDonationDate ? new Date(member.lastDonationDate + 'T00:00:00').toLocaleDateString() : 'N/A'}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </body>
                </html>
            `;

            // Write the content to the new window
            printWindow.document.open();
            printWindow.document.write(htmlContent);
            printWindow.document.close();

            // Add a delay before printing and closing the window
            setTimeout(() => {
                // Ensure the new window is focused
                printWindow.focus();

                // Print the content
                printWindow.print();

                // Add another delay to ensure the print dialog has time to open
                setTimeout(() => {
                    // Close the window after printing
                    printWindow.close();
                }, 1000); // Adjust this delay if needed
            }, 500); // Initial delay to ensure content is rendered
        } else {
            console.error('Failed to open print window.');
        }
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' }); // Switch to landscape mode for more width
        doc.text(getReportTitle(), 20, 10);
        doc.autoTable({
            startY: 20,
            head: [['Member ID', 'Member Name', 'Address', 'City', 'State', 'Zip', 'LifeTime Donation', 'Last Donation', 'Last Donation Date']],
            body: members.map(member => [
                member.memberId,
                `${member.first1} ${member.middle1} ${member.last1}`,
                `${member.address} ${member.address2}`,
                member.city,
                member.state,
                member.zip,
                member.totalDonation ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(member.totalDonation)) : '0',
                member.lastDonation ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(member.lastDonation)) : '0',
                member.lastDonationDate ? new Date(member.lastDonationDate + 'T00:00:00').toLocaleDateString() : 'N/A'
            ]),
            theme: 'striped',
            columnStyles: {
                0: { cellWidth: 20 }, // Member ID
                1: { cellWidth: 45 }, // Member Name
                2: { cellWidth: 50 }, // Address
                3: { cellWidth: 30 }, // City
                4: { cellWidth: 15 }, // State
                5: { cellWidth: 30 }, // Zip
                6: { cellWidth: 30 }, // LifeTime Donation
                7: { cellWidth: 30 }, // Last Donation
                8: { cellWidth: 25 }  // Last Donation Date
            },
            styles: {
                fontSize: 10, // Decrease font size to fit more text in a single line
            }
        });
        // Generate a timestamp for the filename
        const timestamp = getLocalTimestamp();
        const filename = `Member_Donation_Report_${timestamp}.pdf`;
        doc.save(filename);
    };

    const handleDownloadCSV = () => {
        const csvRows = [];
        const headers = ['Member ID', 'First Name', 'Middle Name', 'Last Name', 'Address', 'Address2', 'City', 'State', 'Zip', 'LifeTime Donation', 'Last Donation', 'Last Donation Date'];
        csvRows.push(headers.join(','));

        members.forEach(member => {
            const row = [
                member.memberId,
                `"${member.first1}"`,
                `"${member.middle1}"`,
                `"${member.last1}"`,
                `"${member.address}"`,
                `"${member.address2}"`,
                `"${member.city}"`,
                `"${member.state}"`,
                `"${member.zip}"`,
                member.totalDonation
                    ? member.totalDonation
                    : 0,
                member.lastDonation
                    ? member.lastDonation
                    : 0,
                member.lastDonationDate ? new Date(member.lastDonationDate + 'T00:00:00').toLocaleDateString() : 'N/A'
            ];
            csvRows.push(row.join(','));
        });

        const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const csvURL = window.URL.createObjectURL(csvData);

        const tempLink = document.createElement('a');
        // Generate a timestamp for the filename
        const timestamp = getLocalTimestamp();
        const filename = `Member_Donation_Report_${timestamp}.csv`;
        tempLink.href = csvURL;
        tempLink.setAttribute('download', filename);
        tempLink.click();
    };
    const years = Array.from({ length: new Date().getFullYear() - 1991 }, (_, i) => 1992 + i);
    const donationOptions = [
        { value: '', label: 'Select Amount' },
        { value: '50', label: '$50+' },
        { value: '100', label: '$100+' },
        { value: '250', label: '$250+' },
        { value: '500', label: '$500+' },
        { value: '1000', label: '$1000+' },
        { value: '2000', label: '$2000+' },
        { value: '5000', label: '$5,000+' },
        { value: '10000', label: '$10,000+' },
        { value: '25000', label: '$25,000+' },
        { value: '50000', label: '$50,000+' },
        { value: '100000', label: '$100,000+' },
    ];

    if (loading) return <div>Loading...</div>;
    if (error) return <div className="alert alert-danger" role="alert">Error: {error.message}</div>;

    return (
        <div>
            {role === "admin" && (
                <div className="d-flex justify-content-end mb-2">
                    <Button className="button-spacing no-print small-font me-2" onClick={handleDownloadPDF}>
                        Download PDF
                    </Button>
                    <Button className="button-spacing no-print small-font me-2" onClick={handleDownloadCSV}>
                        Download CSV
                    </Button>
                    <Button className="button-spacing no-print small-font" onClick={handlePrintAll}>
                        Print
                    </Button>
                </div>
            )}
            <div className="mb-4 no-print" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <Row className="w-100">
                    <Col md={6} className="d-flex align-items-center">
                        <label className="form-label small-font font-weight-bold me-2" htmlFor="lifeTimeDonation">
                            LifeTime Donation (Optional)
                        </label>
                        <select
                            id="lifeTimeDonation"
                            className="form-select small-font"
                            value={lifeTimeDonation}
                            onChange={(e) => setLifeTimeDonation(e.target.value)}
                            aria-describedby="lifeTimeDonationHelp"
                        >
                            {donationOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </Col>
                    <Col md={6} className="d-flex align-items-center">
                        <small id="lifeTimeDonationHelp" className="form-text text-muted">
                            Select an amount to filter members based on their lifetime donation.
                        </small>
                    </Col>
                </Row>

                <Row className="w-100 mt-2">
                    <Col md={6} className="d-flex align-items-center">
                        <label className="form-label small-font font-weight-bold me-2" htmlFor="lastDonation">
                            Last Donation (Optional)
                        </label>
                        <select
                            id="lastDonation"
                            className="form-select small-font"
                            value={lastDonation}
                            onChange={(e) => setLastDonation(e.target.value)}
                            aria-describedby="lastDonationHelp"
                        >
                            {donationOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </Col>
                    <Col md={6} className="d-flex align-items-center">
                        <small id="lastDonationHelp" className="form-text text-muted">
                            Select an amount to filter members based on their most recent donation.
                        </small>
                    </Col>
                </Row>

                <Row className="w-100 mt-2">
                    <Col md={6} className="d-flex align-items-center">
                        <label className="form-label small-font font-weight-bold me-2" htmlFor="lastDonationDate">
                            Last Donation Year (Optional)
                        </label>
                        <select
                            id="lastDonationDate"
                            className="form-select small-font"
                            value={lastDonationDate}
                            onChange={(e) => setLastDonationDate(e.target.value)}
                            aria-describedby="lastDonationDateHelp"
                        >
                            <option value="">Select Year</option>
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </Col>
                    <Col md={6} className="d-flex align-items-center">
                        <small id="lastDonationDateHelp" className="form-text text-muted">
                            Select a year to filter members based on their last donation date.
                        </small>
                    </Col>
                </Row>

                <Row className="w-100 mt-2">
                    <Col md={6} className="d-flex align-items-center">
                        <label className="form-label small-font font-weight-bold me-2">
                            Sort by
                        </label>
                    </Col>
                    <Col md={6} className="d-flex align-items-center radio-box">
                        <Form.Check
                            type="radio"
                            id="sort-by-first-name"
                            name="sortBy"
                            value="first1"
                            label="First Name"
                            checked={sortBy === 'first1'}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="small-font font-weight-bold me-2"
                            inline
                        />
                        <Form.Check
                            type="radio"
                            id="sort-by-last-name"
                            name="sortBy"
                            value="last1"
                            label="Last Name"
                            checked={sortBy === 'last1'}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="small-font font-weight-bold"
                            inline
                        />
                    </Col>
                </Row>
            </div>
            <div className="container mt-4">
                <h2 className="mb-4">{getReportTitle()}</h2>
                <Table striped bordered hover size="sm">
                    <thead>
                        <tr>
                            <th>Member ID</th>
                            <th>Member Name</th>
                            <th>Address</th>
                            <th>City</th>
                            <th>State</th>
                            <th>Zip</th>
                            <th>LifeTime Donation</th>
                            <th>Last Donation</th>
                            <th>Last Donation Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentMembers.length > 0 ? (
                            currentMembers.map(member => (
                                <tr key={member.memberId}>
                                    <td>{member.memberId}</td>
                                    <td>{`${member.first1} ${member.middle1} ${member.last1}`}</td>
                                    <td>{`${member.address} ${member.address2}`}</td>
                                    <td>{member.city}</td>
                                    <td>{member.state}</td>
                                    <td>{member.zip}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {member.totalDonation && parseFloat(member.totalDonation) > 0
                                            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(member.totalDonation))
                                            : '0'}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {member.lastDonation && parseFloat(member.lastDonation) > 0
                                            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(member.lastDonation))
                                            : '0'}
                                    </td>
                                    <td>
                                        {member.lastDonationDate
                                            ? new Date(member.lastDonationDate + 'T00:00:00').toLocaleDateString()
                                            : 'N/A'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center">No members found for the selected criteria</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
            {/* Pagination Controls */}
            <div className="d-flex justify-content-center mt-4 no-print">
                <Button onClick={handleFirstPage} disabled={currentPage === 1}>
                    &lt;&lt;  {/* First Page */}
                </Button>
                <Button onClick={handlePreviousPageGroup} disabled={currentPage <= pageGroupSize}>
                    &lt;  {/* Previous Page Group */}
                </Button>

                {/* Dynamically display ellipses before the current group */}
                {currentPage > pageGroupSize && <span>...</span>}  {/* Ellipsis */}

                {/* Render page numbers */}
                {getPageNumbers().map(number => (
                    <Button
                        key={number}
                        onClick={() => paginate(number)}
                        className={number === currentPage ? 'active' : ''}
                    >
                        {number}
                    </Button>
                ))}

                {/* Dynamically display ellipses after the current group */}
                {currentPage + pageGroupSize <= totalPages && <span>...</span>}  {/* Ellipsis */}

                <Button onClick={handleNextPageGroup} disabled={currentPage + pageGroupSize > totalPages}>
                    &gt;  {/* Next Page Group */}
                </Button>
                <Button onClick={handleLastPage} disabled={currentPage === totalPages}>
                    &gt;&gt;  {/* Last Page */}
                </Button>
            </div>
        </div>
    );
};

export default MemberListReport;